import React from "react";
import PropTypes from "prop-types";

/**
 * 機櫃 U 位圖（兩段式新增 ticket 05）。
 *
 * **為什麼自己寫**：模板（Borex）沒有任何對應物。查過 `/ui-*` 與 `/tables-*` 全部頁面，
 * 最接近的是進度條與清單，但這裡要的是「一格一格、由上而下、每格有三種狀態且可點」
 * 的機櫃剖面圖 —— 那是機房領域特有的東西，模板不會有。其餘（標籤、按鈕）仍取自
 * `components/DCKeeper`，這支只負責格子本身。
 *
 * 由上而下＝第 uTotal 格在最上面，跟站在機櫃前面看到的一樣；
 * 反過來畫的話，畫面上的位置跟現場對不起來。
 *
 * 三種狀態的顏色集中在 `_dck-figma.scss` 的 `.dck-umap-slot--*`
 * —— 設計稿還沒定案，顏色最可能改，散在各處會改不完。
 *
 *   <UMap slots={uSlotMap(cabinet, hardware)} onPick={(slot) => ...} />
 */

const STATE_LABEL = { free: "空位", occupied: "已上架", reserved: "已核准待上架" };

/** 一格 U 的說明文字（滑鼠停留與讀螢幕都用這一份，不寫兩套） */
const describe = (slot) => {
  if (slot.state === "free") return `U${slot.u}：空位`;
  const h = slot.hardware || {};
  if (slot.state === "reserved") {
    return `U${slot.u}：${STATE_LABEL.reserved}${h.docNo ? `（單據 ${h.docNo}）` : ""}`;
  }
  return `U${slot.u}：${h.code || ""}${h.name ? `（${h.name}）` : ""}`;
};

export const UMap = ({ slots = [], onPick, emptyHint = "這個機櫃沒有填總U數，畫不出 U 位圖。" }) => {
  if (slots.length === 0) return <p className="text-muted small mb-0">{emptyHint}</p>;
  return (
    <>
      <ul className="dck-umap list-unstyled mb-3">
        {slots.map((slot) => {
          const label = describe(slot);
          // 空位沒有可以點進去的東西 —— 做成按鈕只會讓人按了沒反應
          const clickable = !!onPick && slot.state !== "free";
          const body = (
            <>
              <span className="dck-umap-slot__u">U{slot.u}</span>
              <span className="dck-umap-slot__text">
                {slot.state === "free" ? "—" : (slot.hardware || {}).code}
                {slot.state === "reserved" && (slot.hardware || {}).docNo
                  ? <span className="ms-2 small">{slot.hardware.docNo}</span>
                  : null}
              </span>
            </>
          );
          return (
            <li key={slot.u} className={`dck-umap-slot dck-umap-slot--${slot.state}`} title={label}>
              {clickable ? (
                <button type="button" className="dck-umap-slot__btn" aria-label={label} onClick={() => onPick(slot)}>
                  {body}
                </button>
              ) : (
                <span className="dck-umap-slot__btn" aria-label={label}>{body}</span>
              )}
            </li>
          );
        })}
      </ul>
      <div className="d-flex flex-wrap gap-3 small text-muted">
        {["free", "occupied", "reserved"].map((k) => (
          <span key={k} className="d-inline-flex align-items-center gap-2">
            <i className={`dck-umap-key dck-umap-slot--${k}`} aria-hidden="true" />
            {STATE_LABEL[k]}
          </span>
        ))}
      </div>
    </>
  );
};
UMap.propTypes = {
  /** 由 `uSlotMap(cabinet, hardware)` 產生:[{ u, state, hardware }] */
  slots: PropTypes.arrayOf(PropTypes.shape({
    u: PropTypes.number,
    state: PropTypes.oneOf(["free", "occupied", "reserved"]),
    hardware: PropTypes.object,
  })),
  /** 點一格要做什麼(空位不可點);不給就整張圖唯讀 */
  onPick: PropTypes.func,
  emptyHint: PropTypes.string,
};

export default UMap;
